import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import { setAuthTokens, getUserData, hasAnySession, getActivePortal, getHomePagePath, clearOtherSessions, getAuthToken } from '@/utils/auth';
import { Sparkles } from 'lucide-react';

export default function Login() {
  const [schoolCode, setSchoolCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState('student');
  const [loading, setLoading] = useState(false);
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [isEmailMissing, setIsEmailMissing] = useState(false);
  const [activePortal, setActivePortal] = useState('student');
  const [activeUserType, setActiveUserType] = useState('student');
  const navigate = useNavigate();

  // Check for existing session on mount and redirect if found
  useEffect(() => {
    if (hasAnySession()) {
      const activePortal = getActivePortal();
      if (activePortal) {
        navigate(getHomePagePath(activePortal), { replace: true });
      }
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(
        apiUrl('users/login/'),
        { username, password, school_code: schoolCode.toUpperCase(), selected_role: selectedRole },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000, // 30 seconds timeout
        }
      );
      const data = res.data;

      const portal = data.user_type === 'teacher' ? 'teacher'
        : data.user_type === 'admin' ? 'admin'
          : data.user_type === 'guardian' ? 'guardian'  // Guardians use guardian portal
            : 'student';

      // Clear all other sessions to ensure only one session exists
      clearOtherSessions(portal);

      const userData = {
        id: data.id,
        username: data.username,
        email: data.email,
        user_type: data.user_type,
        full_name: data.full_name,
        school_id: data.school_id,
        school_name: data.school_name,
        school_code: data.school_code,
        grade: data.grade,
        division: data.division,
        division_display: data.division_display,
        grades: data.grades,
        subjects: data.subjects,
        profile_pic_url: data.profile_pic_url,
      };
      if (data.board) {
        userData.board = data.board;
        localStorage.setItem('student_board_id', String(data.board.id));
        localStorage.setItem('student_grade_id', data.grade?.id ? String(data.grade.id) : '');
      }

      setAuthTokens(portal, data.access, data.refresh || '', userData);
      setActivePortal(portal);
      setActiveUserType(data.user_type);

      if (data.user_type === 'student' && data.board && data.grade) {
        window.dispatchEvent(new Event('storage'));
        window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: { userType: 'student' } }));
      }

      if (data.requires_password_change) {
        setRequiresPasswordChange(true);
        setIsEmailMissing(data.is_email_missing);
        setShowPasswordChangeModal(true);
        return;
      }

      redirectBasedOnRole(data.user_type);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data?.error || 'Login failed. Please try again.');
      } else {
        setError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const redirectBasedOnRole = (userType) => {
    switch (userType) {
      case 'student': navigate('/student/learn'); break;
      case 'guardian': navigate('/guardian/community'); break;  // Guardians go to guardian community page
      case 'teacher': navigate('/teacher/dashboard'); break;
      case 'admin': navigate('/admin/dashboard'); break;
      case 'superadmin':
        navigate('/login');
        setError('SuperAdmin access is through the SuperAdmin portal.');
        break;
      default: navigate('/student/learn');
    }
  };

  const handlePasswordChangeSuccess = () => {
    setShowPasswordChangeModal(false);
    setRequiresPasswordChange(false);
    const storedUserData = getUserData(activePortal) || {};
    const userType = storedUserData.user_type || activeUserType;
    if (userType === 'student' && storedUserData.board && storedUserData.grade) {
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: { userType: 'student' } }));
    }
    redirectBasedOnRole(userType);
  };

  return (
    <>
      <div className="flex h-screen font-sans bg-[hsl(var(--main-bg))]">
        <div className="w-full md:w-1/2 flex flex-col justify-center px-10 md:px-20 bg-[hsl(var(--main-bg))] relative">
          <div className="mb-8 flex flex-col items-center text-center">
            <div className="mb-6">
              <img 
                src="/logo_square.png" 
                alt="Budlee AI" 
                className="h-32 w-32 sm:h-40 sm:w-40 object-contain animate-logo-jump" 
              />
            </div>

            <div className="mb-6 w-full max-w-sm text-left">
              <div role="radiogroup" aria-label="Login role" className="flex space-x-1 bg-[hsl(var(--card-bg))] p-1 rounded-lg border border-[hsl(var(--card-border))]">
                {[{ id: 'student', label: '🎓 Student' }, { id: 'teacher', label: '👩‍🏫 Teacher' }, { id: 'admin', label: '👨‍💼 Admin' }, { id: 'guardian', label: '👨‍👩‍👧 Guardian' }].map(opt => (
                  <button key={opt.id} role="radio" aria-checked={selectedRole === opt.id} type="button"
                    onClick={() => setSelectedRole(opt.id)}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${selectedRole === opt.id ? 'bg-[hsl(var(--sidebar-hover))] text-[hsl(var(--green-bright))] shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <h2 className="text-xl font-semibold text-foreground mb-2">Login to Account</h2>
            <p className="text-sm text-muted-foreground">Enter your username and password</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {error && <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">{error}</div>}

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">School Code *</label>
              <input type="text" value={schoolCode} onChange={(e) => setSchoolCode(e.target.value.toUpperCase())}
                placeholder="Enter your school code (e.g., SCH001)"
                className="mt-1 block w-full px-4 py-3 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg shadow-sm focus:ring-2 focus:ring-[hsl(var(--green-bright))] focus:border-[hsl(var(--green-bright))] focus:outline-none text-foreground placeholder:text-muted-foreground"
                required disabled={loading} autoComplete="off" />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Username *</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                className="mt-1 block w-full px-4 py-3 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg shadow-sm focus:ring-2 focus:ring-[hsl(var(--green-bright))] focus:border-[hsl(var(--green-bright))] focus:outline-none text-foreground placeholder:text-muted-foreground"
                required disabled={loading} />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-foreground">Password</label>
                <Link to="/forgot-password" className="text-sm text-[hsl(var(--green-bright))] hover:text-[hsl(var(--teal-vivid))]">Forgot Password?</Link>
              </div>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="block w-full px-4 py-3 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg shadow-sm focus:ring-2 focus:ring-[hsl(var(--green-bright))] focus:border-[hsl(var(--green-bright))] focus:outline-none text-foreground placeholder:text-muted-foreground pr-10"
                  required disabled={loading} />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}>
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white py-3 px-4 rounded-lg hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--green-bright))] focus:ring-offset-2 focus:ring-offset-[hsl(var(--main-bg))] transition-all duration-200 font-medium disabled:opacity-50">
              {loading ? 'Signing In...' : 'Sign In'}
            </button>

            <p className="text-sm text-center mt-4 text-muted-foreground">
              Don't have an account? <span className="text-muted-foreground/70">Contact your administrator to create an account</span>
            </p>
          </form>
        </div>

        <div className="hidden md:block w-1/2 relative bg-gradient-to-br from-[hsl(var(--green-bright))]/10 to-[hsl(var(--teal-vivid))]/10">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center p-8">
              <img
                src="/logo.png"
                alt="Budlee AI Logo"
                className="h-20 mx-auto mb-4 object-contain"
              />
              <img 
                src="/logo_text.png" 
                alt="Budlee AI" 
                className="h-10 mx-auto mb-6 object-contain w-auto" 
              />
              <p className="text-muted-foreground">Your intelligent learning companion</p>
            </div>
          </div>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={showPasswordChangeModal}
        onClose={() => setShowPasswordChangeModal(false)}
        onSuccess={handlePasswordChangeSuccess}
        isFirstTime={requiresPasswordChange}
        requireEmail={isEmailMissing}
        portalOverride={activePortal}
      />
    </>
  );
}


